export const name = "DATE_TRUNC";
export const summary = `Truncates a \`DATE\`, \`DATETIME\`, or \`TIMESTAMP\` value at a particular granularity.`;
export const reference = `
## DATE_TRUNC

Truncates a \`DATE\`, \`DATETIME\`, or \`TIMESTAMP\` value at a particular granularity.

\`\`\`
DATE_TRUNC(date_value, date_granularity)
\`\`\`

### 

\`\`\`
DATE_TRUNC(datetime_value, datetime_granularity)
\`\`\`



\`\`\`
DATE_TRUNC(timestamp_value, timestamp_granularity[, time_zone])
\`\`\`


### Description

Truncates a \`DATE\`, \`DATETIME\`, or \`TIMESTAMP\` value at a particular granularity.


### Definitions

- \`date_value\`: A \`DATE\` value to truncate.
- \`date_granularity\`: The truncation granularity for a \`DATE\` value. Date granularities can be used.
- \`datetime_value\`: A \`DATETIME\` value to truncate.
- \`datetime_granularity\`: The truncation granularity for a \`DATETIME\` value. Date granularities and time granularities can be used.
- \`timestamp_value\`: A \`TIMESTAMP\` value to truncate.
- \`timestamp_granularity\`: The truncation granularity for a \`TIMESTAMP\` value. Date granularities and time granularities can be used.
- \`time_zone\`: A time zone to use with the \`TIMESTAMP\` value. Time zone parts can be used. Use this argument if you want to use a time zone other than the default time zone, UTC, as part of the truncate operation.




### Date granularity definitions

- \`DAY\`: The day in the Gregorian calendar year that contains the value to truncate.

- \`WEEK\`: The first day in the week that contains the value to truncate. Weeks begin on Sundays. \`WEEK\` is equivalent to\`WEEK(SUNDAY)\`.

- \`WEEK(WEEKDAY)\`: The first day in the week that contains the value to truncate. Weeks begin on \`WEEKDAY\`. \`WEEKDAY\` must be one of the following: \`SUNDAY\`, \`MONDAY\`, \`TUESDAY\`, \`WEDNESDAY\`, \`THURSDAY\`, \`FRIDAY\`, or \`SATURDAY\`.

- \`ISOWEEK\`: The first day in the ISO 8601 week that contains the value to truncate. The ISO week begins on Monday. The first ISO week of each ISO year contains the first Thursday of the corresponding Gregorian calendar year.

- \`MONTH\`: The first day in the month that contains the value to truncate.

- \`QUARTER\`: The first day in the quarter that contains the value to truncate.

- \`YEAR\`: The first day in the year that contains the value to truncate.

- \`ISOYEAR\`: The first day in the ISO 8601 week-numbering year that contains the value to truncate. The ISO year is the Monday of the first week where Thursday belongs to the corresponding Gregorian calendar year.




### Time granularity definitions

- \`MICROSECOND\`: If used, nothing is truncated from the value.

- \`MILLISECOND\`: The nearest lesser than or equal millisecond.

- \`SECOND\`: The nearest lesser than or equal second.

- \`MINUTE\`: The nearest lesser than or equal minute.

- \`HOUR\`: The nearest lesser than or equal hour.




### Time zone part definitions

- \`MINUTE\`
- \`HOUR\`
- \`DAY\`
- \`WEEK\`
- \`WEEK(<WEEKDAY>)\`
- \`ISOWEEK\`
- \`MONTH\`
- \`QUARTER\`
- \`YEAR\`
- \`ISOYEAR\`


### Details

The resulting value is always rounded to the beginning of \`granularity\`.


### Return Data Type

The same data type as the first argument passed into this function.


### Examples



\`\`\`
SELECT DATE_TRUNC(DATE '2008-12-25', MONTH) AS month;

/*------------*
 | month |
 +------------+
 | 2008-12-01 |
 *------------*/
\`\`\`

In the following example, the original date falls on a Sunday. Because the \`date_part\` is \`WEEK(MONDAY)\`, \`DATE_TRUNC\` returns the \`DATE\` for the preceding Monday.



\`\`\`
SELECT date AS original, DATE_TRUNC(date, WEEK(MONDAY)) AS truncated
FROM (SELECT DATE('2017-11-05') AS date);

/*------------+------------*
 | original | truncated |
 +------------+------------+
 | 2017-11-05 | 2017-10-30 |
 *------------+------------*/
\`\`\`

In the following example, the original \`date_expression\` is in the Gregorian calendar year 2015. However, \`DATE_TRUNC\` with the \`ISOYEAR\` date part truncates the \`date_expression\` to the beginning of the ISO year, not the Gregorian calendar year. The first Thursday of the 2015 calendar year was 2015-01-01, so the ISO year 2015 begins on the preceding Monday, 2014-12-29. Therefore the ISO year boundary preceding the \`date_expression\` 2015-06-15 is 2014-12-29.



\`\`\`
SELECT
  DATE_TRUNC('2015-06-15', ISOYEAR) AS isoyear_boundary,
  EXTRACT(ISOYEAR FROM DATE '2015-06-15') AS isoyear_number;

/*------------------+----------------*
 | isoyear_boundary | isoyear_number |
 +------------------+----------------+
 | 2014-12-29 | 2015 |
 *------------------+----------------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/date_functions
`;
